import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Camera, Save, CheckCircle, Clock, X, Upload, Leaf, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button, Input, Alert } from '../design-system';
import AuthenticatedImage from '../design-system/AuthenticatedImage';
import { apiPost, getAuthToken } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import analyticsService from '../../services/analyticsService';
import { compressImage } from '../../utils/imageCompression';

// Make analyticsService available globally for api.js to use
if (typeof window !== 'undefined') {
  window.analyticsService = analyticsService;
}

// Task-specific configuration
export const TaskStepOutsideConfig = {};

const TaskStepOutside = ({ task, session, onComplete }) => {
  const { userInfo } = useAuth();
  const [currentStep, setCurrentStep] = useState('instructions');
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoId, setPhotoId] = useState(null);
  const [reflection, setReflection] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasLoadedProgress, setHasLoadedProgress] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Auto-save functionality
  const saveProgress = useCallback(async (skipToast = false) => {
    if (!session?.sessionId) return;

    setIsSaving(true);
    try {
      // Only send metadata, never the actual photo data
      const progressData = {
        sessionId: session.sessionId,
        currentStep: currentStep,
        reflection: reflection
      };

      // Only include photo metadata if we have it (not the actual photo)
      if (photoId) {
        progressData.photoId = photoId;
      }
      if (photoUrl) {
        progressData.photoUrl = photoUrl;
      }

      await apiPost(
        '/.netlify/functions/stepOutsideTaskApi',
        progressData,
        userInfo?.userId
      );

      setLastSaved(new Date());
      if (!skipToast) {
        // Don't show toast for auto-save to avoid spam
      }
    } catch (error) {
      console.error('Error saving progress:', error);
      // Only show error toast for manual saves, not auto-saves
      if (!skipToast) {
        toast.error('Failed to save progress. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [session?.sessionId, userInfo?.userId, currentStep, photoId, photoUrl, reflection]);

  // Debounced auto-save (silent, no toast)
  const debouncedSave = useCallback(
    (() => {
      let timeoutId;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          saveProgress(true); // Skip toast for auto-save
        }, 10000); // 10 seconds
      };
    })(),
    [saveProgress]
  );

  // Load progress from session
  useEffect(() => {
    if (!session || hasLoadedProgress) return;
    
    setHasLoadedProgress(true);
    
    // Load existing data if session has it
    if (session.sessionData?.currentStep) {
      setCurrentStep(session.sessionData.currentStep);
    }
    if (session.sessionData?.photoUrl) {
      setPhotoUrl(session.sessionData.photoUrl);
      setPhotoId(session.sessionData.photoId);
    }
    if (session.sessionData?.reflection) {
      setReflection(session.sessionData.reflection);
    }
  }, [session, hasLoadedProgress]);

  // Auto-save when data changes (but skip if we just uploaded a photo)
  const skipNextAutoSave = useRef(false);
  
  useEffect(() => {
    if (hasLoadedProgress && !skipNextAutoSave.current) {
      debouncedSave();
    }
    // Reset the skip flag after a delay
    if (skipNextAutoSave.current) {
      const timer = setTimeout(() => {
        skipNextAutoSave.current = false;
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentStep, photoId, photoUrl, reflection, hasLoadedProgress, debouncedSave]);

  const handleStart = () => {
    setCurrentStep('camera');
    // Save progress silently when moving to camera step
    saveProgress(true).catch(err => console.error('Error saving on start:', err));
  };

  // Helper function to get device/browser info for error tracking
  const getDeviceInfo = () => {
    const ua = navigator.userAgent;
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    
    return {
      isMobile,
      isIOS,
      isAndroid,
      userAgent: ua,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      connectionType: navigator.connection?.effectiveType || 'unknown',
      isOnline: navigator.onLine
    };
  };

  const handleFileSelect = async (e) => {
    console.log('📸 handleFileSelect called', { 
      hasFiles: !!e.target.files, 
      fileCount: e.target.files?.length,
      isCamera: e.target === cameraInputRef.current,
      isGallery: e.target === fileInputRef.current
    });
    
    // Small delay for camera files to ensure they're fully available (mobile issue)
    const isCamera = e.target === cameraInputRef.current;
    if (isCamera) {
      console.log('📸 Camera file detected, adding small delay for file availability...');
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    const file = e.target.files?.[0];
    const inputSource = isCamera ? 'camera' : 'gallery';
    
    console.log('📸 File selected', { 
      inputSource, 
      fileName: file?.name || 'unnamed',
      fileSize: file?.size || 0,
      fileSizeMB: file?.size ? (file.size / (1024 * 1024)).toFixed(2) : '0',
      fileType: file?.type || 'unknown',
      lastModified: file?.lastModified,
      hasFile: !!file,
      isCameraFile: isCamera
    });
    
    if (!file) {
      // User cancelled or no file selected
      console.log('📸 No file selected, returning');
      // Reset inputs to allow selecting again
      if (isCamera && cameraInputRef.current) {
        cameraInputRef.current.value = '';
      }
      if (e.target === fileInputRef.current && fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    
    // Camera-specific validation and logging
    if (isCamera) {
      console.log('📸 Camera file details:', {
        name: file.name || 'no name',
        size: file.size,
        type: file.type || 'no type',
        lastModified: file.lastModified,
        hasName: !!file.name,
        hasType: !!file.type,
        hasSize: file.size > 0
      });
    }

    // Track file selection attempt with detailed info
    analyticsService.track('photo_upload_started', {
      taskTypeId: task?.taskTypeId || 'task_024',
      sessionId: session?.sessionId,
      inputSource, // 'camera' or 'gallery'
      fileName: file.name || 'unnamed',
      fileSize: file.size,
      fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
      fileType: file.type || 'unknown',
      lastModified: file.lastModified,
      lastModifiedDate: new Date(file.lastModified).toISOString(),
      hasName: !!file.name,
      hasType: !!file.type,
      timestamp: new Date().toISOString(),
      ...getDeviceInfo()
    });

    // Validate file exists and has size
    if (!file || file.size === 0) {
      const errorInfo = {
        errorType: 'empty_file',
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session?.sessionId,
        inputSource,
        ...getDeviceInfo()
      };
      
      analyticsService.track('photo_upload_error', errorInfo);
      toast.error('The selected file is empty. Please try again.');
      // Reset inputs
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      return;
    }

    // Validate file type
    if (!file.type || !file.type.startsWith('image/')) {
      const errorInfo = {
        errorType: 'invalid_file_type',
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session?.sessionId,
        inputSource,
        fileType: file.type || 'unknown',
        ...getDeviceInfo()
      };
      
      analyticsService.track('photo_upload_error', errorInfo);
      toast.error('Please select an image file (JPEG, PNG, etc.)');
      // Reset inputs
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      return;
    }

    // Validate file size
    if (file.size > 10 * 1024 * 1024) {
      const errorInfo = {
        errorType: 'file_too_large',
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session?.sessionId,
        inputSource,
        fileSize: file.size,
        fileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
        ...getDeviceInfo()
      };
      
      analyticsService.track('photo_upload_error', errorInfo);
      toast.error('Image size must be less than 10MB. Please choose a smaller image.');
      // Reset inputs
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      return;
    }

    setPhoto(file);
    setIsUploading(true);
    console.log('📸 Starting file processing', { inputSource, fileSize: file.size });

    // Validate session exists
    if (!session || !session.sessionId) {
      toast.error('No active session found. Please refresh the page and try again.');
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      return;
    }

    let compressedFile = null;
    let uploadToken = null;
    
    try {
      // Step 1: Compress image
      console.log('📸 Starting image compression...');
      const compressionStartTime = Date.now();
      
      analyticsService.track('photo_compression_started', {
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session?.sessionId,
        inputSource,
        originalFileSize: file.size,
        originalFileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
        fileType: file.type,
        timestamp: new Date().toISOString(),
        ...getDeviceInfo()
      });

      try {
        compressedFile = await compressImage(file, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.85,
          maxSizeMB: 4
        });
        
        const compressionDuration = Date.now() - compressionStartTime;
        const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);
        
        console.log('📸 Image compression complete', {
          originalSize: file.size,
          originalSizeMB: (file.size / (1024 * 1024)).toFixed(2),
          compressedSize: compressedFile.size,
          compressedSizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
          compressionRatio: `${compressionRatio}%`,
          duration: compressionDuration
        });

        analyticsService.track('photo_compression_complete', {
          taskTypeId: task?.taskTypeId || 'task_024',
          sessionId: session?.sessionId,
          inputSource,
          originalFileSize: file.size,
          originalFileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
          compressedFileSize: compressedFile.size,
          compressedFileSizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
          compressionRatio,
          compressionDuration,
          timestamp: new Date().toISOString(),
          ...getDeviceInfo()
        });
      } catch (compressionError) {
        console.error('📸 Compression failed, using original file:', compressionError);
        // If compression fails, use original file
        compressedFile = file;
        
        analyticsService.track('photo_compression_failed', {
          taskTypeId: task?.taskTypeId || 'task_024',
          sessionId: session?.sessionId,
          inputSource,
          errorMessage: compressionError.message,
          usingOriginal: true,
          timestamp: new Date().toISOString(),
          ...getDeviceInfo()
        });
      }

      // Step 2: Get upload token/URL
      console.log('📸 Requesting upload token...');
      const tokenStartTime = Date.now();
      
      analyticsService.track('photo_upload_token_requested', {
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session.sessionId,
        inputSource,
        fileSize: compressedFile.size,
        fileSizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
        fileName: compressedFile.name || 'photo.jpg',
        mimeType: compressedFile.type,
        timestamp: new Date().toISOString(),
        ...getDeviceInfo()
      });

      const tokenResponse = await apiPost(
        '/.netlify/functions/get-upload-url',
        {
          sessionId: session.sessionId,
          fileName: compressedFile.name || 'photo.jpg',
          mimeType: compressedFile.type,
          fileSize: compressedFile.size
        },
        userInfo?.userId
      );

      if (!tokenResponse.uploadToken || !tokenResponse.uploadUrl) {
        throw new Error('Failed to get upload token');
      }

      uploadToken = tokenResponse.uploadToken;
      const tokenDuration = Date.now() - tokenStartTime;
      
      console.log('📸 Upload token received', {
        photoId: tokenResponse.photoId,
        blobKey: tokenResponse.blobKey,
        duration: tokenDuration
      });

      analyticsService.track('photo_upload_token_received', {
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session.sessionId,
        inputSource,
        photoId: tokenResponse.photoId,
        tokenDuration,
        timestamp: new Date().toISOString(),
        ...getDeviceInfo()
      });

      // Step 3: Upload file directly as binary
      console.log('📸 Starting direct binary upload...');
      const uploadStartTime = Date.now();
      
      analyticsService.track('photo_direct_upload_started', {
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session.sessionId,
        inputSource,
        fileSize: compressedFile.size,
        fileSizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
        fileName: compressedFile.name || 'photo.jpg',
        mimeType: compressedFile.type,
        photoId: tokenResponse.photoId,
        timestamp: new Date().toISOString(),
        ...getDeviceInfo()
      });

      // Get auth token for upload
      const authTokenResult = getAuthToken();
      const authToken = authTokenResult instanceof Promise ? await authTokenResult : authTokenResult;

      if (!authToken) {
        throw new Error('No authentication token available');
      }

      // Upload file as binary blob (not FormData for simpler parsing)
      const uploadUrl = `${tokenResponse.uploadUrl}?uploadToken=${encodeURIComponent(uploadToken)}&fileName=${encodeURIComponent(compressedFile.name || 'photo.jpg')}&mimeType=${encodeURIComponent(compressedFile.type)}`;
      
      const uploadResponse = await fetch(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'X-User-ID': userInfo?.userId || '',
          'Content-Type': compressedFile.type || 'image/jpeg',
          ...(sessionStorage.getItem('theraengage_demo_mode') === 'true' ? { 'X-Demo-Mode': 'true' } : {})
        },
        body: compressedFile // Send file directly as blob
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          errorData = { error: errorText };
        }
        throw new Error(errorData.error || `Upload failed with status ${uploadResponse.status}`);
      }

      const response = await uploadResponse.json();
      const uploadDuration = Date.now() - uploadStartTime;

      console.log('📸 Direct upload complete', {
        photoId: response.photo?.photoId,
        photoUrl: response.photo?.url,
        duration: uploadDuration
      });

      analyticsService.track('photo_direct_upload_complete', {
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session.sessionId,
        inputSource,
        fileSize: compressedFile.size,
        fileSizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
        uploadDuration,
        photoId: response.photo?.photoId,
        timestamp: new Date().toISOString(),
        ...getDeviceInfo()
      });

      if (response.photo) {
        console.log('📸 Photo data received', { 
          photoId: response.photo.photoId,
          photoUrl: response.photo.url
        });
        // Track successful upload
        analyticsService.track('photo_upload_success', {
          taskTypeId: task?.taskTypeId || 'task_024',
          sessionId: session?.sessionId,
          inputSource,
          originalFileSize: file.size,
          originalFileSizeMB: (file.size / (1024 * 1024)).toFixed(2),
          compressedFileSize: compressedFile.size,
          compressedFileSizeMB: (compressedFile.size / (1024 * 1024)).toFixed(2),
          fileType: compressedFile.type,
          uploadDuration,
          photoId: response.photo.photoId,
          uploadMethod: 'direct_binary',
          ...getDeviceInfo()
        });

        // Skip auto-save that might trigger from state changes
        skipNextAutoSave.current = true;
        
        setPhotoUrl(response.photo.url);
        setPhotoId(response.photo.photoId);
        toast.success('Photo uploaded successfully');
        
        // Save progress (only metadata, not photo data) and move to reflection step
        // Use a small delay to ensure state is updated
        setTimeout(async () => {
          try {
            // Track progress save start
            analyticsService.track('photo_progress_save_started', {
              taskTypeId: task?.taskTypeId || 'task_024',
              sessionId: session?.sessionId,
              photoId: response.photo.photoId,
              timestamp: new Date().toISOString()
            });
            
            const saveStartTime = Date.now();
            await saveProgress(true); // Silent save after photo upload
            const saveDuration = Date.now() - saveStartTime;
            
            // Track progress save complete
            analyticsService.track('photo_progress_save_complete', {
              taskTypeId: task?.taskTypeId || 'task_024',
              sessionId: session?.sessionId,
              photoId: response.photo.photoId,
              saveDuration,
              timestamp: new Date().toISOString()
            });
          } catch (saveError) {
            console.error('Error saving progress after photo upload:', saveError);
            // Track progress save error
            analyticsService.track('photo_progress_save_error', {
              taskTypeId: task?.taskTypeId || 'task_024',
              sessionId: session?.sessionId,
              photoId: response.photo.photoId,
              errorMessage: saveError.message,
              timestamp: new Date().toISOString()
            });
            // Don't block user from continuing if save fails
          }
        }, 100);
        
        setCurrentStep('reflection');
      } else {
        const errorInfo = {
          errorType: 'upload_response_missing',
          errorMessage: 'Upload succeeded but no photo data was returned',
          taskTypeId: task?.taskTypeId || 'task_024',
          sessionId: session?.sessionId,
          inputSource,
          fileSize: file.size,
          fileType: file.type,
          uploadDuration,
          ...getDeviceInfo()
        };
        
        analyticsService.track('photo_upload_error', errorInfo);
        throw new Error('Upload succeeded but no photo data was returned');
      }
    } catch (error) {
      // Calculate sizes for error tracking
      const originalFileSizeMB = file?.size ? (file.size / (1024 * 1024)).toFixed(2) : 'unknown';
      const compressedFileSizeMB = compressedFile?.size ? (compressedFile.size / (1024 * 1024)).toFixed(2) : 'unknown';
      
      // Extract error response body (limit length for PostHog)
      let errorResponseBody = null;
      if (error.responseBody) {
        const bodyStr = typeof error.responseBody === 'string' 
          ? error.responseBody 
          : JSON.stringify(error.responseBody);
        errorResponseBody = bodyStr.substring(0, 1000); // Limit to 1000 chars
      }
      
      // Always log error details
      const errorDetails = {
        errorName: error.name,
        errorMessage: error.message,
        errorStack: error.stack?.substring(0, 1000), // Limit stack trace
        inputSource,
        timestamp: new Date().toISOString(),
        // Include request details if available
        originalFileSizeMB,
        compressedFileSizeMB,
        uploadMethod: 'direct_binary',
        // Include error properties
        errorStatus: error.status,
        errorResponseBody: errorResponseBody,
        errorOriginalUrl: error.originalUrl,
        errorRequestBodySizeMB: error.requestBodySizeMB
      };
      
      // Also store on window for debugging (visible in mobile browser console)
      if (typeof window !== 'undefined') {
        window.lastPhotoUploadError = errorDetails;
        window.lastPhotoUploadErrorFull = error; // Store full error object too
      }
      
      // Extract detailed error information from apiPost error message
      // apiPost format: "HTTP error! status: 500, message: {...}"
      // authenticatedFetch format: "Server error - Please try again later"
      let httpStatus = null;
      let httpStatusText = null;
      let responseBody = null;
      let errorName = error.name || 'Unknown';
      let errorMessage = error.message || 'Unknown error';
      let originalErrorMessage = errorMessage;
      
      // Parse error message from apiPost - it includes the response body!
      // Format: "HTTP error! status: 500, message: {\"error\":\"Stream body too big\"}"
      const httpErrorMatch = errorMessage.match(/HTTP error!\s*status:\s*(\d+)(?:,\s*message:\s*(.+))?/);
      if (httpErrorMatch) {
        httpStatus = parseInt(httpErrorMatch[1]);
        if (httpErrorMatch[2]) {
          responseBody = httpErrorMatch[2].substring(0, 500); // Limit length
          originalErrorMessage = httpErrorMatch[2]; // This is the actual server error
          
          // Try to parse as JSON to extract error message
          try {
            const cleaned = responseBody.trim();
            if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
              const parsed = JSON.parse(cleaned);
              if (parsed.error) {
                originalErrorMessage = parsed.error;
              } else if (parsed.message) {
                originalErrorMessage = parsed.message;
              }
            }
          } catch (e) {
            // Not JSON, use as-is
          }
        }
      }
      
      // Check for specific error patterns from authenticatedFetch
      // These happen when response.status >= 500 before apiPost can read body
      if (errorMessage.includes('Server error - Please try again later')) {
        httpStatus = httpStatus || 500;
        errorName = 'ServerError';
        // Note: We don't have the actual error message here because
        // authenticatedFetch throws before reading the body
      } else if (errorMessage.includes('Network error - Please check your connection')) {
        errorName = 'NetworkError';
      } else if (errorMessage.includes('Unauthorized - Please log in again')) {
        httpStatus = 401;
        errorName = 'UnauthorizedError';
      } else if (errorMessage.includes('Access denied - Insufficient permissions')) {
        httpStatus = 403;
        errorName = 'ForbiddenError';
      }
      
      // Try to parse JSON from response body if we have it
      let parsedResponseBody = null;
      if (responseBody) {
        try {
          const cleaned = responseBody.trim();
          if (cleaned.startsWith('{') || cleaned.startsWith('[')) {
            parsedResponseBody = JSON.parse(cleaned);
          }
        } catch (e) {
          // Not JSON, keep as string
        }
      }
      
      // Determine error type based on error patterns and HTTP status
      let errorType = 'upload_failed';
      
      // Check for specific error patterns from api.js error messages first
      if (errorMessage.includes('Server error - Please try again later')) {
        httpStatus = httpStatus || 500;
        errorName = 'ServerError';
        errorType = 'server_error';
      } else if (errorMessage.includes('Network error - Please check your connection')) {
        errorName = 'NetworkError';
        errorType = 'network_error';
      } else if (errorMessage.includes('Unauthorized - Please log in again')) {
        httpStatus = 401;
        errorName = 'UnauthorizedError';
        errorType = 'permission_denied';
      } else if (errorMessage.includes('Access denied - Insufficient permissions')) {
        httpStatus = 403;
        errorName = 'ForbiddenError';
        errorType = 'permission_denied';
      } else if (errorMessage.includes('No authentication token available')) {
        errorName = 'AuthTokenError';
        errorType = 'authentication_error';
      } else if (httpStatus === 413) {
        errorType = 'file_too_large_server';
      } else if (httpStatus === 400) {
        errorType = 'invalid_file_format_server';
      } else if (httpStatus === 401 || httpStatus === 403) {
        errorType = 'permission_denied';
      } else if (httpStatus >= 500) {
        errorType = 'server_error';
      } else if (errorName === 'NetworkError' || errorMessage.includes('Network error')) {
        errorType = 'network_error';
      } else if (errorName === 'TypeError') {
        errorType = 'type_error';
      } else if (errorName === 'AuthTokenError') {
        errorType = 'authentication_error';
      }
      
      // Track error with detailed context
      const errorInfo = {
        errorType,
        errorName, // Error class name (TypeError, Error, etc.)
        errorMessage: errorMessage.substring(0, 500), // Full error message from api.js
        originalErrorMessage: originalErrorMessage.substring(0, 500), // Actual server error message
        errorStack: error.stack?.substring(0, 1000), // Increased stack trace length
        taskTypeId: task?.taskTypeId || 'task_024',
        sessionId: session?.sessionId,
        inputSource,
        fileName: file?.name || 'unknown',
        fileSize: file?.size || 0,
        fileSizeMB: file?.size ? (file.size / (1024 * 1024)).toFixed(2) : 'unknown',
        fileType: file?.type || 'unknown',
        fileLastModified: file?.lastModified,
        compressedFileSize: compressedFile?.size || 0,
        compressedFileSizeMB: compressedFile?.size ? (compressedFile.size / (1024 * 1024)).toFixed(2) : '0',
        httpStatus,
        httpStatusText,
        responseBody: responseBody?.substring(0, 500), // Server response body if available (limited)
        parsedError: parsedResponseBody?.error || null, // Parsed error from JSON response
        parsedMessage: parsedResponseBody?.message || null, // Parsed message from JSON response
        timestamp: new Date().toISOString(),
        ...getDeviceInfo()
      };
      
      // Track error to PostHog
      console.log('📸 Tracking error to PostHog:', errorInfo);
      analyticsService.track('photo_upload_error', errorInfo);
      
      // Provide more specific error messages for user
      console.log('📸 Showing error toast to user');
      let userErrorMessage = 'Failed to upload photo. ';
      if (error.message) {
        userErrorMessage += error.message;
      } else if (error.response?.status === 413) {
        userErrorMessage += 'File is too large.';
      } else if (error.response?.status === 400) {
        userErrorMessage += 'Invalid file format.';
      } else if (error.response?.status === 401 || error.response?.status === 403) {
        userErrorMessage += 'Permission denied. Please refresh and try again.';
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        userErrorMessage += 'Network error. Please check your connection and try again.';
      } else {
        userErrorMessage += 'Please try again.';
      }
      
      toast.error(userErrorMessage);
      setPhoto(null);
    } finally {
      setIsUploading(false);
      // Reset file inputs to allow selecting the same file again if needed
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleRetakePhoto = () => {
    setPhoto(null);
    setPhotoUrl(null);
    setPhotoId(null);
    setCurrentStep('camera');
  };

  const handleReflectionChange = (value) => {
    setReflection(value);
  };

  const handleContinue = () => {
    // Validate that reflection text is provided
    if (!reflection.trim()) {
      toast.error('Please share what stood out to you');
      return;
    }
    
    setCurrentStep('complete');
    // Save progress silently when moving to complete step
    saveProgress(true).catch(err => console.error('Error saving on continue:', err));
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      const completionData = {
        photoId: photoId,
        photoUrl: photoUrl,
        reflection: reflection.trim(),
        completedAt: new Date().toISOString()
      };

      // Save completion data to session
      await apiPost(
        '/.netlify/functions/stepOutsideTaskApi',
        {
          sessionId: session.sessionId,
          action: 'complete',
          completionData: completionData
        },
        userInfo?.userId
      );

      // Then complete the task session
      await apiPost(
        '/.netlify/functions/complete-task-session',
        {
          sessionId: session.sessionId,
          taskId: task.taskId
        },
        userInfo?.userId
      );

      toast.success('Task completed!');
      onComplete();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManualSave = async () => {
    try {
      await saveProgress(false); // Show toast for manual save
      toast.success('Progress saved!');
    } catch (error) {
      // Error toast is already shown in saveProgress
    }
  };

  // Render instructions screen
  const renderInstructionsScreen = () => (
    <div className="h-full flex flex-col items-center justify-center px-4 py-8">
      <Card variant="elevated" padding="large" className="max-w-2xl w-full text-center">
        <div className="mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Leaf className="h-10 w-10 text-green-600" />
          </div>
        </div>
        
        <div className="space-y-4 mb-8">
          <p className="text-xl text-gray-700 leading-relaxed">
            Go outside for a few minutes.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed">
            Look around slowly.
          </p>
          <p className="text-xl text-gray-700 leading-relaxed">
            When something catches your eye — even something tiny — take a photo.
          </p>
        </div>

        <Alert variant="info" className="mb-6 text-left">
          <p className="text-sm text-blue-700">
            If you can't go outside, find a window or take a photo of something natural nearby.
          </p>
        </Alert>

        <Button
          onClick={handleStart}
          variant="primary"
          size="large"
          className="w-full"
        >
          Take Photo
        </Button>
      </Card>
    </div>
  );

  // Render camera screen
  const renderCameraScreen = () => (
    <div className="h-full flex flex-col max-w-6xl mx-auto px-3 md:px-4 py-4">
      <Card variant="elevated" padding="medium" className="h-full flex flex-col overflow-hidden">
        <div className="mb-4">
          <h1 className="text-base md:text-lg font-semibold text-gray-900">
            Capture something that caught your eye
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Take a photo of anything that stands out to you
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 mb-4">
          {photoUrl ? (
            <div className="relative bg-gray-50 rounded-lg p-2">
              <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                <AuthenticatedImage 
                  src={photoUrl} 
                  alt="Your photo" 
                  className="w-full h-auto max-h-96 object-contain"
                />
              </div>
              <Button
                onClick={handleRetakePhoto}
                variant="ghost"
                size="small"
                className="absolute top-4 right-4 bg-white hover:bg-gray-100 border border-gray-300 shadow-sm"
                title="Retake photo"
              >
                <X className="h-4 w-4 text-gray-700" />
                Retake
              </Button>
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <div className="flex flex-col items-center space-y-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                  <Camera className="h-6 w-6 text-gray-400" />
                </div>
                <div>
                  <p className="text-sm text-gray-600 mb-2">Take a photo or upload an image</p>
                  <div className="flex gap-2 justify-center">
                    <Button
                      onClick={() => cameraInputRef.current?.click()}
                      disabled={isUploading}
                      variant="outline"
                      size="small"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Take Photo
                    </Button>
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      variant="outline"
                      size="small"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Hidden file inputs */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
                aria-label="Upload image from gallery"
              />
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
                aria-label="Take photo with camera"
              />
            </div>
          )}
          
          {isUploading && (
            <div className="mt-2 text-sm text-gray-500 text-center">
              Uploading photo...
            </div>
          )}

          {photoUrl && (
            <div className="mt-4">
              <Button
                onClick={() => setCurrentStep('reflection')}
                variant="primary"
                size="medium"
                className="w-full"
              >
                Continue
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );

  // Render reflection screen
  const renderReflectionScreen = () => (
    <div className="h-full flex flex-col max-w-6xl mx-auto px-3 md:px-4 py-4">
      <Card variant="elevated" padding="medium" className="h-full flex flex-col overflow-hidden">
        <div className="mb-4">
          <h1 className="text-base md:text-lg font-semibold text-gray-900">
            What stood out to you today?
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Share your thoughts about what you noticed
          </p>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 mb-4">
          <div className="mb-4">
            <Input
              type="textarea"
              value={reflection}
              onChange={(e) => handleReflectionChange(e.target.value)}
              placeholder="Type what you noticed... (e.g., the color of the leaves, the sound of birds, the texture of the sidewalk, something unexpected...)"
              rows={10}
              className="resize-none w-full"
            />
          </div>

          <Alert variant="info">
            <p className="text-sm text-blue-700">
              There are no right or wrong answers. Write whatever caught your attention.
            </p>
          </Alert>
        </div>

        <div className="flex justify-between items-center gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleManualSave}
            disabled={isSaving}
            variant="ghost"
            size="medium"
            className="flex items-center"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Progress'}
          </Button>

          <Button
            onClick={handleContinue}
            disabled={!reflection.trim()}
            variant="primary"
            size="medium"
            className="flex items-center"
          >
            Continue
            <CheckCircle className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </Card>
    </div>
  );

  // Render completion screen
  const renderCompletionScreen = () => {
    return (
      <div className="h-full flex flex-col max-w-6xl mx-auto px-3 md:px-4 py-4">
        <Card variant="elevated" padding="medium" className="h-full flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto min-h-0 mb-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Thank you for taking a moment to notice the world around you.
              </h1>
            </div>

            {photoUrl && (
              <div className="mb-6">
                <div className="border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                  <AuthenticatedImage 
                    src={photoUrl} 
                    alt="Your photo" 
                    className="w-full h-auto max-h-64 object-contain"
                  />
                </div>
              </div>
            )}

            {reflection && (
              <div className="mb-6">
                <h2 className="text-sm font-medium text-gray-700 mb-3">What you noticed:</h2>
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {reflection}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t border-gray-200">
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              variant="primary"
              size="medium"
              className="flex items-center"
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              {isSubmitting ? 'Completing...' : 'Complete Task'}
            </Button>
          </div>
        </Card>
      </div>
    );
  };

  // Main render
  return (
    <div className="h-full">
      {currentStep === 'instructions' && renderInstructionsScreen()}
      {currentStep === 'camera' && renderCameraScreen()}
      {currentStep === 'reflection' && renderReflectionScreen()}
      {currentStep === 'complete' && renderCompletionScreen()}
      
      {lastSaved && currentStep !== 'instructions' && (
        <div className="fixed bottom-4 left-4 flex items-center text-xs text-gray-500 bg-white px-3 py-2 rounded-lg shadow-sm border border-gray-200">
          <Clock className="h-3 w-3 mr-1" />
          Last saved: {lastSaved.toLocaleTimeString()}
        </div>
      )}
    </div>
  );
};

// Summary for patient dashboard
TaskStepOutside.Summary = ({ task, session }) => {
  const hasPhoto = session?.sessionData?.photoUrl;
  const hasReflection = session?.sessionData?.reflection;
  const currentStep = session?.sessionData?.currentStep || 'instructions';
  
  let statusText = 'Not started';
  if (currentStep === 'complete') {
    statusText = 'Completed';
  } else if (hasReflection) {
    statusText = 'Reflection added';
  } else if (hasPhoto) {
    statusText = 'Photo taken';
  } else if (currentStep !== 'instructions') {
    statusText = 'In progress';
  }
  
  return (
    <div className="step-outside-summary">
      <div className="flex items-center space-x-2">
        <Leaf className="h-4 w-4 text-green-600" />
        <span className="text-xs text-gray-500">{statusText}</span>
      </div>
    </div>
  );
};

// Results summary for therapist view
TaskStepOutside.ResultsSummary = ({ task, completionData, patientName }) => {
  if (!completionData) return null;

  return (
    <Card variant="elevated" padding="large">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">Step Outside & Capture a Moment - Results</h4>
      
      <div className="flex flex-col md:flex-row items-start space-y-1 md:space-y-0 md:space-x-2 mb-1">
        <div className="flex-shrink-0">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Leaf className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h5 className="text-lg font-medium text-gray-900">{task.title}</h5>
          <p className="text-sm text-gray-500">
            Completed by {patientName} on {new Date(completionData.completedAt || Date.now()).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* Photo Display */}
      {completionData.photoUrl && (
        <div className="border-t pt-1 mb-1">
          <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Camera className="h-4 w-4 mr-1" />
            Patient's Photo:
          </h6>
          <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
            <AuthenticatedImage 
              src={completionData.photoUrl} 
              alt="Patient's photo" 
              className="w-full h-auto max-h-96 object-contain"
            />
          </div>
        </div>
      )}

      {/* Reflection Display */}
      {completionData.reflection && (
        <div className="border-t pt-1">
          <h6 className="text-sm font-medium text-gray-700 mb-1 flex items-center">
            <Sparkles className="h-4 w-4 mr-1" />
            What Patient Noticed:
          </h6>
          <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
            {completionData.reflection}
          </div>
        </div>
      )}
    </Card>
  );
};

// Task assignment form for therapist
TaskStepOutside.AssignTaskForm = ({ formData, setFormData, taskType }) => {
  return (
    <Card variant="default" padding="large">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Instruction for Patient (Optional)
          </label>
          <Input
            type="textarea"
            value={formData.instruction || ''}
            onChange={(e) => setFormData(prev => ({...prev, instruction: e.target.value}))}
            placeholder="Enter optional custom instructions for the patient..."
            rows={3}
          />
          <p className="text-xs text-gray-500 mt-1">
            Optional: Add custom instructions. If left empty, the default task description will be used.
          </p>
        </div>
      </div>
    </Card>
  );
};

export default TaskStepOutside;

